import { useEffect, useRef } from "react";

const FOCUSABLE_SELECTOR = [
  "button:not([disabled])",
  "a[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

// resolveScope (facultatif) : element ou le focus reste piege, s'il differe du
// dialogue (ex. fenetre ouverte par-dessus a l'interieur du dialogue).
export function useModalAccessibility(dialogRef, { active = true, resolveScope = null } = {}) {
  const resolveScopeRef = useRef(resolveScope);
  resolveScopeRef.current = resolveScope;
  useEffect(() => {
    if (!active || typeof document === "undefined") return undefined;

    const dialog = dialogRef.current;
    if (!dialog) return undefined;

    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusableElements = () =>
      [...(resolveScopeRef.current?.() || dialog).querySelectorAll(FOCUSABLE_SELECTOR)].filter(
        (element) => element.getClientRects().length > 0
      );

    const initialTarget =
      dialog.querySelector("[data-autofocus]") || focusableElements()[0] || dialog;
    const initialFocusFrame = window.requestAnimationFrame(() => {
      // Une interaction ou une autre fenetre peut avoir pris le focus avant
      // cette image : ne pas ecraser ce choix avec le focus d'ouverture.
      if (!dialog.isConnected) return;
      if (document.activeElement !== previouslyFocused && document.activeElement !== document.body) return;
      initialTarget.focus({ preventScroll: true });
    });

    const handleKeyDown = (event) => {
      if (event.key !== "Tab") return;
      const elements = focusableElements();
      if (!elements.length) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const first = elements[0];
      const last = elements[elements.length - 1];
      const scope = resolveScopeRef.current?.() || dialog;
      if (!scope.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    dialog.addEventListener("keydown", handleKeyDown);
    return () => {
      window.cancelAnimationFrame(initialFocusFrame);
      dialog.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused?.isConnected) previouslyFocused.focus({ preventScroll: true });
    };
  }, [active, dialogRef]);
}
